import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter, ChapterVariation } from "../../types";
import { IdeTabStrip, type IdeTabItem } from "./IdeTabStrip";
import type { ChapterStatus } from "./SidebarChaptersPanel";
import { ManuscriptEditorPane, type ManuscriptEditorHandle } from "./ManuscriptEditorPane";
import { ChapterCompareOverlay } from "./ChapterCompareOverlay";
import type { ParagraphDiffHunk } from "../../../shared/chapterParagraphDiff";
import { LuHeadphones } from "react-icons/lu";
import { ChapterDetailsPopover } from "./ChapterDetailsPopover";

export function EditorWorkbench<T extends string>({
  tabs,
  manuscriptTabId,
  briefTabId,
  briefPanel,
  storyCraftTabId,
  storyCraftPanel,
  historyTabId,
  historyPanel,
  charactersTabId,
  charactersPanel,
  locationsTabId,
  locationsPanel,
  timelineTabId,
  timelinePanel,
  themesTabId,
  themesPanel,
  activeTab,
  onTabChange,
  activeChapter,
  manuscriptEditorRef,
  manuscriptReloadToken,
  manuscriptThemePreset,
  manuscriptEditable,
  selectedChapterCommentId,
  onManuscriptCommentSelect,
  onManuscriptDraftFlush,
  onManuscriptTyping,
  onChapterTitleChange,
  onChapterCaptionChange,
  onChapterStatusChange,
  onChapterTagsChange,
  onTabFieldChange,
  onOpenAudio,
  compareVariation,
  compareBaseContent,
  onCompareApply,
  onCompareClose,
  onCompareVariationContentChange,
  onRequestComment,
  findReplaceOpen,
  onFindReplaceOpenChange,
  liveCollabYDoc = null,
  liveCollabAwareness = null,
  liveCollabUser = null,
  liveCollabStatus = null,
  liveCollabSessionKey = "local",
  allowCitations = false,
  allowImages = false,
  citationOptions = [],
  imageOptions = [],
  onInsertCitation,
  onInsertImage,
  resolveImageSrc,
}: {
  tabs: readonly IdeTabItem<T>[];
  manuscriptTabId: T;
  /** Canonical editable chapter synopsis with its own generation controls. */
  briefTabId?: T;
  briefPanel?: ReactNode;
  /** When set with `storyCraftPanel`, the Story Craft tab shows structured feedback (original desktop parity). */
  storyCraftTabId?: T;
  storyCraftPanel?: ReactNode;
  /** When set with `historyPanel`, shows continuous chapter change history. */
  historyTabId?: T;
  historyPanel?: ReactNode;
  /** When set with `charactersPanel`, the Characters tab shows the roster panel. */
  charactersTabId?: T;
  charactersPanel?: ReactNode;
  /** When set with `locationsPanel`, the Locations tab shows the gazetteer panel. */
  locationsTabId?: T;
  locationsPanel?: ReactNode;
  /** When set with `timelinePanel`, the Timeline tab shows the beats panel. */
  timelineTabId?: T;
  timelinePanel?: ReactNode;
  /** When set with `themesPanel`, the Themes & Motifs tab shows the roster panel. */
  themesTabId?: T;
  themesPanel?: ReactNode;
  activeTab: T;
  onTabChange: (id: T) => void;
  activeChapter: Chapter;
  manuscriptEditorRef: RefObject<ManuscriptEditorHandle | null>;
  manuscriptReloadToken: number;
  manuscriptThemePreset: string | null;
  manuscriptEditable: boolean;
  selectedChapterCommentId: string | null;
  onManuscriptCommentSelect: (commentId: string | null) => void;
  onManuscriptDraftFlush: (payload: {
    chapterId: string;
    content: string;
    comments: Chapter["comments"];
  }) => void;
  onManuscriptTyping?: () => void;
  onChapterTitleChange: (title: string) => void;
  onChapterCaptionChange: (caption: string) => void;
  onChapterStatusChange: (status: ChapterStatus) => void;
  onChapterTagsChange: (tags: string[]) => void;
  onTabFieldChange: (tabId: T, value: string) => void;
  onOpenAudio?: () => void;
  compareVariation?: ChapterVariation | null;
  compareBaseContent?: string;
  onCompareApply?: (mergedContent: string, hunks: ParagraphDiffHunk[]) => void;
  onCompareClose?: () => void;
  onCompareVariationContentChange?: (content: string) => void;
  onRequestComment?: () => void;
  findReplaceOpen?: boolean;
  onFindReplaceOpenChange?: (open: boolean) => void;
  liveCollabYDoc?: import("yjs").Doc | null;
  liveCollabAwareness?: import("y-protocols/awareness").Awareness | null;
  liveCollabUser?: { name: string; color: string } | null;
  liveCollabStatus?: string | null;
  liveCollabSessionKey?: string;
  allowCitations?: boolean;
  allowImages?: boolean;
  citationOptions?: Array<{ id: string; label: string }>;
  imageOptions?: Array<{ id: string; title: string; src?: string }>;
  onInsertCitation?: (citationId: string) => void;
  onInsertImage?: (assetId: string) => void;
  resolveImageSrc?: (assetId: string) => string | null;
}) {
  const tabLabel = tabs.find((tab) => tab.id === activeTab)?.label ?? "Chapter";
  const showBrief = Boolean(briefTabId && briefPanel && activeTab === briefTabId);
  const showStoryCraft =
    Boolean(storyCraftTabId && storyCraftPanel && activeTab === storyCraftTabId);
  const showHistory = Boolean(historyTabId && historyPanel && activeTab === historyTabId);
  const showCharacters =
    Boolean(charactersTabId && charactersPanel && activeTab === charactersTabId);
  const showLocations =
    Boolean(locationsTabId && locationsPanel && activeTab === locationsTabId);
  const showTimeline =
    Boolean(timelineTabId && timelinePanel && activeTab === timelineTabId);
  const showThemes = Boolean(themesTabId && themesPanel && activeTab === themesTabId);
  const showManuscript = activeTab === manuscriptTabId;
  const [formatToolbarHost, setFormatToolbarHost] = useState<HTMLDivElement | null>(null);
  const editorBodyRef = useRef<HTMLDivElement | null>(null);

  // WKWebView can collapse nested flex children to 0px; enforce a usable body height.
  useLayoutEffect(() => {
    const body = editorBodyRef.current;
    if (!body) return;
    const enforce = () => {
      const panel = body.closest("[data-workbench-editor-inner]") as HTMLElement | null;
      const panelH = panel?.clientHeight ?? 0;
      const chrome = panel?.querySelector("[data-workbench-editor-chrome]") as HTMLElement | null;
      const chromeH = chrome?.offsetHeight ?? 0;
      const floor = Math.max(256, Math.floor(panelH - chromeH - 8));
      if (body.clientHeight < 120 && floor > 120) {
        body.style.minHeight = `${floor}px`;
        body.style.flex = "1 1 auto";
      }
    };
    enforce();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(enforce) : null;
    ro?.observe(body);
    if (body.parentElement) ro?.observe(body.parentElement);
    return () => ro?.disconnect();
  }, [activeTab, activeChapter.id]);

  useEffect(() => {
    const body = editorBodyRef.current;
    if (body) body.style.minHeight = "";
  }, [activeChapter.id]);

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden" aria-label="Editor panel">
      <Card
        className="m-1 flex h-full min-h-0 flex-1 flex-col gap-0 overflow-auto rounded-lg border border-border bg-card py-0 shadow-sm ring-0"
        data-workbench-editor-inner
      >
        <div
          className="flex shrink-0 flex-col gap-0 border-b border-border bg-muted/15"
          data-workbench-editor-chrome
        >
          <IdeTabStrip tabs={tabs} activeId={activeTab} onSelect={onTabChange} ariaLabel="Chapter sections" />
          <div className="grid grid-cols-1 items-center gap-x-[0.45rem] gap-y-[0.35rem] p-2 min-[821px]:grid-cols-[minmax(0,1fr)_minmax(6.5rem,7.5rem)_auto]">
            <Input
              aria-label="Chapter title"
              className="min-w-0"
              value={activeChapter.title}
              onChange={(event) => onChapterTitleChange(event.target.value)}
            />
            <Select
              value={activeChapter.metadata.status}
              onValueChange={(value) => onChapterStatusChange(value as ChapterStatus)}
            >
              <SelectTrigger className="min-w-26" size="sm" aria-label="Chapter status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_review">In review</SelectItem>
                <SelectItem value="final">Final</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex min-w-0 items-center justify-end gap-1.5">
              {showManuscript ? (
                <div ref={setFormatToolbarHost} className="manuscript-format-toolbar-host" />
              ) : (
                <div className="manuscript-format-toolbar-host" aria-hidden />
              )}
              <ChapterDetailsPopover
                caption={activeChapter.caption ?? ""}
                tags={activeChapter.metadata.tags}
                onCaptionChange={onChapterCaptionChange}
                onTagsChange={onChapterTagsChange}
              />
              {onOpenAudio ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={onOpenAudio}
                  aria-label="Open audio workspace"
                  title="Open audio workspace"
                >
                  <LuHeadphones aria-hidden />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        <div
          ref={editorBodyRef}
          className="flex min-h-64 flex-[1_1_auto] flex-col gap-2 overflow-auto p-2"
          data-editor-body="true"
        >
          <div
            className="flex min-h-64 min-w-0 flex-[1_1_auto] flex-col overflow-auto"
            data-editor-main="true"
          >
            {compareVariation && onCompareApply && onCompareClose ? (
              <ChapterCompareOverlay
                chapterTitle={activeChapter.title}
                variation={compareVariation}
                liveBaseContent={compareBaseContent}
                onApply={onCompareApply}
                onClose={onCompareClose}
                onVariationContentChange={onCompareVariationContentChange}
              />
            ) : showManuscript ? (
              <div className="editor-textarea manuscript-surface manuscript-well">
                {liveCollabYDoc ? (
                  <div
                    className="border-b border-border px-3 py-1 text-xs text-muted-foreground"
                    role="status"
                    data-live-collab-status={liveCollabStatus || "on"}
                  >
                    Live collab: {liveCollabStatus || "on"}
                  </div>
                ) : null}
                <ManuscriptEditorPane
                  key={`${activeChapter.id}:${liveCollabSessionKey}`}
                  ref={manuscriptEditorRef}
                  chapterId={activeChapter.id}
                  chapterContent={activeChapter.content}
                  chapterComments={activeChapter.comments}
                  editable={manuscriptEditable}
                  themePreset={manuscriptThemePreset}
                  selectedCommentId={selectedChapterCommentId}
                  reloadToken={manuscriptReloadToken}
                  formatToolbarHost={formatToolbarHost}
                  onCommentSelect={onManuscriptCommentSelect}
                  onTyping={onManuscriptTyping}
                  onDraftFlush={onManuscriptDraftFlush}
                  onRequestComment={onRequestComment}
                  findReplaceOpen={findReplaceOpen}
                  onFindReplaceOpenChange={onFindReplaceOpenChange}
                  liveCollabYDoc={liveCollabYDoc}
                  liveCollabAwareness={liveCollabAwareness}
                  liveCollabUser={liveCollabUser}
                  liveCollabStatus={liveCollabStatus}
                  allowCitations={allowCitations}
                  allowImages={allowImages}
                  citationOptions={citationOptions}
                  imageOptions={imageOptions}
                  onInsertCitation={onInsertCitation}
                  onInsertImage={onInsertImage}
                  resolveImageSrc={resolveImageSrc}
                />
              </div>
            ) : showBrief ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{briefPanel}</div>
            ) : showStoryCraft ? (
              // Story Craft supplies its own frame and scroll region, so it does
              // not opt into `.manuscript-surface` (whose legacy rules add a
              // second scroll container and clipped the panel header).
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{storyCraftPanel}</div>
            ) : showHistory ? (
              <div className="manuscript-surface">{historyPanel}</div>
            ) : showCharacters ? (
              <div className="manuscript-surface">{charactersPanel}</div>
            ) : showLocations ? (
              <div className="manuscript-surface">{locationsPanel}</div>
            ) : showTimeline ? (
              <div className="manuscript-surface h-full overflow-auto px-4 pb-5 pt-3">{timelinePanel}</div>
            ) : showThemes ? (
              <div className="manuscript-surface">{themesPanel}</div>
            ) : (
              <div className="flex min-h-full flex-1 flex-col gap-2 rounded-lg bg-card px-6 pb-7 pt-5">
                <label
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  htmlFor="editor-summary-field"
                >
                  Chapter brief
                </label>
                <Textarea
                  id="editor-summary-field"
                  aria-label={`${tabLabel} content`}
                  className="min-h-56 flex-1 resize-y border-0 bg-transparent p-0 text-[0.92rem] leading-relaxed shadow-none focus-visible:ring-0"
                  placeholder="Brief recap of what happens in this chapter…"
                  value={(activeChapter.tabData as Record<string, string>)[activeTab as string] ?? ""}
                  onChange={(event) => onTabFieldChange(activeTab, event.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </Card>
    </section>
  );
}
